import React, { useState } from 'react';
import _ from 'lodash';
import { type PortalSettings, type FieldValues, type ButtonItemAction, ItemActionsFieldValue } from '@gorilla/portal-shared/src/schemas';
import { SectionDescriptor, type ItemActionsFieldDescriptor } from '@gorilla/portal-shared/src/types';
import { Box, Flex, Icon, Button, IconButton, Toggle, Tooltip, TextField, Badge, Text } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { Dropdown } from '@vibe/core';
import {
  Robot as RobotIcon,
  AddSmall as AddSmallIcon,
  Workflow as WorkflowIcon,
  LearnMore as LearnMoreIcon,
  Duplicate as DuplicateIcon,
  Delete,
  Pin,
} from '@vibe/icons';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';
import { createRandomId } from '../../lib/utils';
import { monday } from '../../services/monday';
import { ConditionsModal, areConditionsValid } from '../ConditionsModal';

type ItemActionsFieldProps = {
  value: ItemActionsFieldValue;
  fieldDescriptor: ItemActionsFieldDescriptor;
  sectionDescriptor: SectionDescriptor;
  portalSettings: PortalSettings;
  sectionFieldValues: FieldValues;
  clientBoardSubitemsBoardId: number | null;
  onChange: (value: ItemActionsFieldValue) => void;
};

export function ItemActionsField({
  value,
  fieldDescriptor,
  portalSettings,
  onChange,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
}: ItemActionsFieldProps) {
  const [conditionsModalActionId, setConditionsModalActionId] = useState<string | null>(null);

  const { boardSourceType, board } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  // ✅ Normalize safely (handles old/partial values where actions might be missing)
  const valueNormalized: ItemActionsFieldValue = value?.actions ? value : { actions: [] };
  const actions = valueNormalized.actions || [];
  const enabled = actions.length > 0;

  const conditionsModalActionIdx = actions.findIndex((action) => action.id === conditionsModalActionId);
  const conditionsModalAction = conditionsModalActionIdx !== -1 ? actions[conditionsModalActionIdx] : null;

  const buttonStyleOptions = [
    { label: 'Primary button', value: 'primary' },
    { label: 'Secondary button', value: 'secondary' },
  ];

  if (!board || !boardSourceType || boardSourceType === 'subitems' || (board?.name && board.name.startsWith('Subitems of'))) {
    return null;
  }

  return (
    <>
      {board && conditionsModalAction ? (
        <ConditionsModal
          board={board}
          conditions={conditionsModalAction.conditions || []}
          conditionsOperator={conditionsModalAction.conditionsOperator}
          isOpen={true}
          onClose={(newConditions, newOperator) => {
            if (newConditions !== null) {
              const newAction = { ...conditionsModalAction, conditions: newConditions, conditionsOperator: newOperator };
              const newActions = [...actions];
              newActions[conditionsModalActionIdx] = newAction;

              onChange({ actions: newActions });
            }

            setConditionsModalActionId(null);
          }}
          isButtonCondition={true}
        />
      ) : null}

      <Box
        border={true}
        borderColor="layoutBorderColor"
        className="section__field section__field--actionbuttons section__field--nomargin item--actions--wrapper"
      >
        <Box backgroundColor="greyBackgroundColor">
          <Box padding="medium">
            <Flex gap={0}>
              <Flex gap={8}>
                <Icon icon={RobotIcon} size={22} style={{ transform: 'translate3d(0,-1px,0)' }} />
                <Text type="text2" weight="bold" align="start" ellipsis={true}>
                  Action buttons
                </Text>
                <div>
                  <Tooltip
                    title="What's this?"
                    content="Conditionally display buttons on each item that trigger predefined automations. Click to learn more."
                    moveBy={{ main: 5, secondary: -15 }}
                    position="right"
                    showOnDialogEnter={false}
                  >
                    <div
                      onClick={() => {
                        monday.execute('openLinkInTab', {
                          url: 'https://getgorilla.app/products/client-portal/docs/user-guides/action-buttons',
                        });
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Icon icon={LearnMoreIcon} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
                    </div>
                  </Tooltip>
                </div>
              </Flex>

              <Box style={{ marginLeft: 'auto' }}>
                <IconButton
                  icon={AddSmallIcon}
                  kind={actions.length >= 2 ? 'primary' : 'secondary'}
                  size="small"
                  color="primary"
                  disabled={actions.length >= 2}
                  onClick={() => {
                    if (actions.length >= 2) return;

                    const existingKinds = new Set(actions.map((a) => a.kind));
                    const nextKind: 'primary' | 'secondary' = existingKinds.has('primary') ? 'secondary' : 'primary';

                    const newAction: ButtonItemAction = {
                      id: createRandomId(),
                      type: 'button',
                      label: '',
                      actionId: '',
                      kind: nextKind,
                      conditions: null,
                    };

                    onChange({ actions: [...actions, newAction] });
                  }}
                />
              </Box>
            </Flex>
          </Box>

          {actions.length > 0 ? (
            <Box paddingEnd="medium" paddingBottom="medium" paddingStart="medium" style={{ overflow: 'visible' }}>
              <Flex direction="column" gap={14}>
                {actions.map((action, index) => {
                  let selectedButtonStyleOption = buttonStyleOptions.find((option) => option.value === action.kind);
                  if (!selectedButtonStyleOption) selectedButtonStyleOption = buttonStyleOptions[0];

                  return (
                    <React.Fragment key={action.id}>
                      <Box style={{ overflow: 'visible', width: '100%' }}>
                        <Flex gap={14} align="end">
                          {action.conditions && action.conditions.length ? (
                            <Badge
                              // @ts-ignore
                              color={areConditionsValid(board, action.conditions) ? 'primary' : 'notification'}
                            >
                              <IconButton
                                icon={WorkflowIcon}
                                kind="secondary"
                                size="small"
                                onClick={() => setConditionsModalActionId(action.id)}
                                className="custom__deletepagebutton custom__whitebutton_"
                              />
                            </Badge>
                          ) : (
                            <Tooltip content="Show button conditionally" position="left" showOnDialogEnter={false}>
                              <IconButton
                                icon={WorkflowIcon}
                                kind="secondary"
                                size="small"
                                onClick={() => setConditionsModalActionId(action.id)}
                                className="custom__deletepagebutton custom__whitebutton_"
                              />
                            </Tooltip>
                          )}

                          <Flex gap={14} align="end" style={{ flex: 1 }}>
                            <div style={{ width: '50%' }}>
                              <Flex direction="column" gap={4} style={{ width: '100%' }}>
                                <div style={{ width: 'inherit', minWidth: '166px' }}>
                                  <Dropdown
                                    size="small"
                                    clearable={false}
                                    placeholder="Button style"
                                    options={buttonStyleOptions}
                                    value={dropdownValue(selectedButtonStyleOption)}
                                    onChange={(e: any) => {
                                      const newActions = _.cloneDeep([...actions]);
                                      // @ts-ignore
                                      newActions[index].kind = e.value;
                                      onChange({ actions: newActions });
                                    }}
                                  />
                                </div>
                              </Flex>
                            </div>

                            <div style={{ width: '50%' }}>
                              <TextField
                                placeholder={index < 1 ? 'e.g. Accept' : 'e.g. Reject'}
                                size="small"
                                maxLength={15}
                                value={action.label}
                                onChange={(newVal) => {
                                  const newActions = _.cloneDeep([...actions]);
                                  // @ts-ignore
                                  newActions[index].label = newVal;
                                  onChange({ actions: newActions });
                                }}
                              />
                            </div>

                            <div style={{ width: '50%' }}>
                              <TextField
                                placeholder={index < 1 ? 'e.g. accept-id' : 'e.g. reject-id'}
                                size="small"
                                value={action.actionId}
                                onChange={(newVal) => {
                                  const newActions = _.cloneDeep([...actions]);
                                  // @ts-ignore
                                  newActions[index].actionId = newVal;
                                  onChange({ actions: newActions });
                                }}
                                icon={action.actionId ? DuplicateIcon : ''}
                                onIconClick={() => {
                                  navigator.clipboard.writeText(action.actionId as string);
                                }}
                              />
                            </div>
                          </Flex>

                          <Flex gap={8}>
                            <IconButton
                              icon={Delete}
                              kind="tertiary"
                              size="small"
                              onClick={() => {
                                const newActions = _.cloneDeep([...actions]);
                                newActions.splice(index, 1);
                                onChange({ actions: newActions });
                              }}
                              className="custom__deletepagebutton"
                            />
                          </Flex>
                        </Flex>
                      </Box>
                    </React.Fragment>
                  );
                })}
              </Flex>
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  );
}
